import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ShieldCheck, X } from "lucide-react";
import { api } from "../lib/api";

// Mandatory Driver acknowledgment (account-level, versioned). The declaration
// text and version come from the server verbatim; acceptance is recorded
// server-side (never localStorage). Declining closes the flow — no booking is
// created and no payment is authorized.
export default function DriverConsent({ onAccept, onDecline }) {
  const [doc, setDoc] = useState(null);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { api.driverConsentStatus().then(setDoc).catch((e) => setError(e.message)); }, []);

  const accept = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.driverConsentAccept();
      onAccept?.();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
         role="dialog" aria-modal="true" aria-label="Driver acknowledgment" data-testid="driver-consent-modal">
      <div className="w-full max-w-lg rounded-2xl border border-[#27272A] bg-[#0D1114] p-6 sm:p-7 shadow-2xl shadow-black/60 max-h-[85dvh] flex flex-col">
        <div className="flex items-start justify-between gap-4 shrink-0">
          <div>
            <p className="meta-label !text-[#FF3B45]">Driver acknowledgment</p>
            <h3 className="type-h3 mt-1.5" data-testid="driver-consent-title">
              {doc?.title || "Parking Rules, Vehicle Responsibility & Booking Terms"}
            </h3>
          </div>
          <button type="button" onClick={onDecline} aria-label="Close" data-testid="driver-consent-close"
                  className="text-zinc-500 hover:text-white transition-colors shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>
        {!doc && !error && <div className="mt-6 h-40 rounded-xl bg-white/[0.03] animate-pulse" />}
        {error && <p className="mt-4 text-sm text-red-400" role="alert" data-testid="driver-consent-error">{error}</p>}
        {doc && (
          <>
            <div className="mt-4 flex-1 min-h-0 overflow-y-auto overscroll-contain rounded-xl border border-[#1F2328] bg-[#080B0D] p-4"
                 data-testid="driver-consent-text" tabIndex={0}>
              {doc.text.split("\n\n").map((p, i) => (
                <p key={i} className="text-sm text-zinc-300 leading-relaxed mb-3 last:mb-0">{p}</p>
              ))}
              <div className="pt-3 border-t border-[#1F2328] flex flex-wrap gap-x-4 gap-y-1.5">
                {doc.links.map((l) => (
                  <Link key={l.href + l.label} to={l.href} target="_blank" rel="noreferrer"
                        className="text-xs text-[#FF3B45] underline underline-offset-2 hover:text-white transition-colors">
                    {l.label}
                  </Link>
                ))}
              </div>
            </div>
            <p className="mt-3 text-[11px] text-zinc-600 shrink-0" data-testid="driver-consent-version">
              Declaration version {doc.required_version} · Terms {doc.terms_version}
            </p>
            <label className="mt-4 flex items-start gap-2.5 cursor-pointer shrink-0">
              <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)}
                     data-testid="driver-consent-checkbox" className="mt-0.5 w-4 h-4 accent-[#E50914]" />
              <span className="text-sm text-zinc-200">{doc.checkbox_label}</span>
            </label>
            <div className="mt-5 grid grid-cols-2 gap-3 shrink-0">
              <button type="button" onClick={onDecline} disabled={busy} data-testid="driver-consent-decline"
                      className="btn-secondary !h-11 text-sm">Decline</button>
              <button type="button" onClick={accept} disabled={!checked || busy} data-testid="driver-consent-accept"
                      className="btn-primary !h-11 text-sm disabled:opacity-40 disabled:cursor-not-allowed">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                Accept &amp; Continue
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
